
interface Remedy {
  id: string;
  name: string;
  scientificName: string;
  commonNames: string[];
  description: string;
  mainIndications: string[];
  potencies: string[];
  symptoms: string[];
  mentalSymptoms: string[];
  physicalSymptoms: string[];
  modalities: {
    worse: string[];
    better: string[];
  };
  constitution: string;
  category: 'mineral' | 'plant' | 'animal' | 'nosode' | 'sarcodio' | 'autotherapeutic' | 'isotherapeutic';
  image: string;
  created: string;
  updated: string;
}

class RemediesDataManager {
  private static instance: RemediesDataManager;
  private remedies: Remedy[] = [];

  private constructor() {
    this.initializeDefaultRemedies();
  }

  public static getInstance(): RemediesDataManager {
    if (!RemediesDataManager.instance) {
      RemediesDataManager.instance = new RemediesDataManager();
    }
    return RemediesDataManager.instance;
  }

  private initializeDefaultRemedies(): void {
    this.remedies = [
      {
        id: '1',
        name: 'Arnica Montana',
        scientificName: 'Arnica montana L.',
        commonNames: ['Arnica', 'Tabaco-de-montanha', 'Betônica-das-montanhas'],
        description: 'Remédio homeopático amplamente conhecido por suas propriedades no tratamento de traumas físicos, contusões e lesões. É considerado o "primeiro socorro" da homeopatia.',
        mainIndications: [
          'Traumas físicos e emocionais',
          'Contusões e hematomas',
          'Dores musculares',
          'Cirurgias e procedimentos',
          'Choque emocional'
        ],
        potencies: ['6CH', '12CH', '30CH', '200CH'],
        symptoms: [
          'Dor após trauma',
          'Sensação de machucado',
          'Medo de ser tocado',
          'Nega estar doente'
        ],
        mentalSymptoms: [
          'Choque emocional',
          'Recusa ajuda',
          'Nega gravidade da situação',
          'Irritabilidade pós-trauma'
        ],
        physicalSymptoms: [
          'Hematomas',
          'Dores musculares',
          'Sensibilidade ao toque',
          'Hemorragias capilares'
        ],
        modalities: {
          worse: ['Toque', 'Movimento', 'Tempo úmido', 'Repouso prolongado'],
          better: ['Movimento suave', 'Posição horizontal', 'Compressas frias']
        },
        constitution: 'Pessoas robustas, pletóricas, com tendência a traumas',
        category: 'plant',
        image: 'https://readdy.ai/api/search-image?query=Arnica%20montana%20flower%2C%20medicinal%20plant%2C%20yellow%20mountain%20flower%2C%20homeopathic%20remedy%20source%2C%20natural%20healing%20herb%2C%20botanical%20illustration%20style%2C%20clean%20white%20background%2C%20centered%20composition%2C%20soft%20natural%20lighting%2C%20high%20detail%20quality&width=300&height=200&seq=1&orientation=landscape',
        created: '2024-01-15',
        updated: '2024-01-15'
      },
      {
        id: '2',
        name: 'Nux Vomica',
        scientificName: 'Strychnos nux-vomica L.',
        commonNames: ['Noz-vômica', 'Fava-de-santo-inácio'],
        description: 'Remédio fundamental para distúrbios digestivos e do sistema nervoso, especialmente relacionados ao estresse moderno e excesso de estímulos.',
        mainIndications: [
          'Distúrbios digestivos',
          'Estresse e irritabilidade',
          'Insônia por preocupações',
          'Ressaca e intoxicações',
          'Cólicas e espasmos'
        ],
        potencies: ['6CH', '12CH', '30CH', '200CH', '1M'],
        symptoms: [
          'Hipersensibilidade',
          'Irritabilidade extrema',
          'Distúrbios digestivos',
          'Insônia nas primeiras horas'
        ],
        mentalSymptoms: [
          'Irritabilidade',
          'Impaciência',
          'Competitividade excessiva',
          'Ansiedade por trabalho'
        ],
        physicalSymptoms: [
          'Náuseas matinais',
          'Constipação',
          'Dores de cabeça',
          'Cãibras musculares'
        ],
        modalities: {
          worse: ['Manhã cedo', 'Frio seco', 'Raiva', 'Excesso alimentar'],
          better: ['Calor', 'Repouso', 'Tarde/noite', 'Movimentação']
        },
        constitution: 'Pessoas ativas, competitivas, com vida sedentária e estressante',
        category: 'plant',
        image: 'https://readdy.ai/api/search-image?query=Strychnos%20nux%20vomica%20seeds%2C%20homeopathic%20remedy%20source%2C%20medicinal%20seeds%2C%20natural%20healing%20medicine%2C%20botanical%20illustration%20style%2C%20clean%20white%20background%2C%20centered%20composition%2C%20soft%20natural%20lighting%2C%20high%20detail%20quality&width=300&height=200&seq=2&orientation=landscape',
        created: '2024-01-16',
        updated: '2024-01-16'
      },
      {
        id: '3',
        name: 'Pulsatilla',
        scientificName: 'Pulsatilla nigricans',
        commonNames: ['Anêmona', 'Flor-do-vento'],
        description: 'Remédio conhecido por sua aplicação em pessoas de temperamento suave e variável, com sintomas que mudam constantemente.',
        mainIndications: [
          'Distúrbios hormonais femininos',
          'Problemas respiratórios',
          'Distúrbios digestivos',
          'Estados emocionais instáveis',
          'Problemas de circulação'
        ],
        potencies: ['6CH', '12CH', '30CH', '200CH'],
        symptoms: [
          'Sintomas variáveis',
          'Busca por consolo',
          'Melhora ao ar livre',
          'Piora com calor'
        ],
        mentalSymptoms: [
          'Emotividade',
          'Busca por atenção',
          'Timidez',
          'Ciúmes e possessividade'
        ],
        physicalSymptoms: [
          'Secreções espessas e amareladas',
          'Problemas menstruais',
          'Problemas digestivos',
          'Varizes e má circulação'
        ],
        modalities: {
          worse: ['Calor', 'Ambiente fechado', 'Alimentos gordurosos', 'Tarde'],
          better: ['Ar livre', 'Movimento suave', 'Companhia', 'Frio']
        },
        constitution: 'Pessoas de temperamento suave, emotivas, que buscam apoio',
        category: 'plant',
        image: 'https://readdy.ai/api/search-image?query=Pulsatilla%20nigricans%20flower%2C%20purple%20anemone%20flower%2C%20homeopathic%20remedy%20source%2C%20medicinal%20wildflower%2C%20botanical%20illustration%20style%2C%20clean%20white%20background%2C%20centered%20composition%2C%20soft%20natural%20lighting%2C%20high%20detail%20quality&width=300&height=200&seq=3&orientation=landscape',
        created: '2024-01-17',
        updated: '2024-01-17'
      },
      {
        id: '4',
        name: 'Calcarea Carbonica',
        scientificName: 'Calcarea carbonica',
        commonNames: ['Carbonato de cálcio', 'Cal de ostra'],
        description: 'Remédio fundamental para distúrbios do metabolismo do cálcio e desenvolvimento, especialmente em crianças e pessoas de constituição lenta.',
        mainIndications: [
          'Distúrbios do crescimento',
          'Problemas ósseos e dentários',
          'Obesidade e metabolismo lento',
          'Sudorese excessiva',
          'Ansiedade e medos'
        ],
        potencies: ['6CH', '12CH', '30CH', '200CH', '1M'],
        symptoms: [
          'Metabolismo lento',
          'Sudorese da cabeça',
          'Medo do escuro',
          'Fadiga mental'
        ],
        mentalSymptoms: [
          'Ansiedade antecipatória',
          'Medo de altura',
          'Preocupação com a saúde',
          'Dificuldade de concentração'
        ],
        physicalSymptoms: [
          'Sudorese na cabeça',
          'Problemas dentários',
          'Obesidade',
          'Constipação crônica'
        ],
        modalities: {
          worse: ['Frio úmido', 'Esforço mental', 'Dentição', 'Lua cheia'],
          better: ['Tempo seco', 'Deitado do lado afetado', 'Constipação']
        },
        constitution: 'Pessoas de constituição linfática, metabolismo lento, tendência ao sobrepeso',
        category: 'mineral',
        image: 'https://readdy.ai/api/search-image?query=Calcium%20carbonate%20crystal%20structure%2C%20white%20mineral%2C%20homeopathic%20remedy%20source%2C%20medicinal%20mineral%2C%20scientific%20illustration%20style%2C%20clean%20white%20background%2C%20centered%20composition%2C%20soft%20natural%20lighting%2C%20high%20detail%20quality&width=300&height=200&seq=4&orientation=landscape',
        created: '2024-01-18',
        updated: '2024-01-18'
      },
      {
        id: '5',
        name: 'Thymulin',
        scientificName: 'Thymulin',
        commonNames: ['Hormônio do timo', 'Fator tímico'],
        description: 'Bioterápico preparado a partir do hormônio do timo, utilizado para estimular o sistema imunológico e regular a resposta imune do organismo.',
        mainIndications: [
          'Imunodeficiência',
          'Infecções recorrentes',
          'Alergias e hipersensibilidade',
          'Doenças autoimunes',
          'Estimulação imunológica'
        ],
        potencies: ['7CH', '9CH', '15CH', '30CH'],
        symptoms: [
          'Infecções frequentes',
          'Baixa resistência',
          'Fadiga crônica',
          'Cicatrização lenta'
        ],
        mentalSymptoms: [
          'Desânimo',
          'Falta de energia vital',
          'Sensação de fraqueza',
          'Desmotivação'
        ],
        physicalSymptoms: [
          'Infecções respiratórias',
          'Problemas de pele',
          'Linfonodos aumentados',
          'Cicatrização deficiente'
        ],
        modalities: {
          worse: ['Mudanças de tempo', 'Estresse', 'Fadiga', 'Infecções'],
          better: ['Repouso', 'Alimentação adequada', 'Ambiente saudável']
        },
        constitution: 'Pessoas com sistema imune debilitado ou hiperativo',
        category: 'sarcodio',
        image: 'https://readdy.ai/api/search-image?query=Thymus%20gland%20medical%20illustration%2C%20immune%20system%20organ%2C%20biotherapeutic%20homeopathic%20remedy%20source%2C%20anatomical%20drawing%20style%2C%20clean%20white%20background%2C%20centered%20composition%2C%20soft%20natural%20lighting%2C%20high%20detail%20quality&width=300&height=200&seq=5&orientation=landscape',
        created: '2024-01-19',
        updated: '2024-01-19'
      },
      {
        id: '6',
        name: 'Auto-Sanguis',
        scientificName: 'Autologous blood',
        commonNames: ['Sangue autólogo', 'Autonosódio sanguíneo'],
        description: 'Tautoterápico preparado a partir do próprio sangue do paciente, utilizado para tratar condições crônicas e estimular a auto-regulação do organismo.',
        mainIndications: [
          'Doenças crônicas recidivantes',
          'Processos inflamatórios crônicos',
          'Alergias persistentes',
          'Doenças de pele crônicas',
          'Estimulação da auto-cura'
        ],
        potencies: ['12CH', '30CH', '200CH', '1M'],
        symptoms: [
          'Sintomas recorrentes',
          'Inflamação crônica',
          'Resistência a tratamentos',
          'Padrões repetitivos'
        ],
        mentalSymptoms: [
          'Padrões mentais fixos',
          'Resistência a mudanças',
          'Comportamentos repetitivos',
          'Auto-sabotagem'
        ],
        physicalSymptoms: [
          'Sintomas que voltam sempre',
          'Inflamações recorrentes',
          'Alergias persistentes',
          'Doenças que não curam'
        ],
        modalities: {
          worse: ['Estresse emocional', 'Mudanças bruscas', 'Supressão'],
          better: ['Estabilidade', 'Rotina saudável', 'Equilíbrio emocional']
        },
        constitution: 'Pessoas com tendência a cronificação de sintomas',
        category: 'autotherapeutic',
        image: 'https://readdy.ai/api/search-image?query=Blood%20cells%20microscopic%20view%2C%20red%20blood%20cells%20and%20white%20blood%20cells%2C%20autotherapeutic%20homeopathic%20remedy%20source%2C%20medical%20illustration%20style%2C%20clean%20white%20background%2C%20centered%20composition%2C%20soft%20natural%20lighting%2C%20high%20detail%20quality&width=300&height=200&seq=6&orientation=landscape',
        created: '2024-01-20',
        updated: '2024-01-20'
      }
    ];
  }

  // Métodos para gerenciar remédios
  public getAllRemedies(): Remedy[] {
    return [...this.remedies];
  }

  public getRemedyById(id: string): Remedy | undefined {
    return this.remedies.find(remedy => remedy.id === id);
  }

  public addRemedy(remedy: Omit<Remedy, 'id' | 'created' | 'updated'>): Remedy {
    const newRemedy: Remedy = {
      ...remedy,
      id: Date.now().toString(),
      created: new Date().toISOString().split('T')[0],
      updated: new Date().toISOString().split('T')[0]
    };
    this.remedies.push(newRemedy);
    return newRemedy;
  }

  public updateRemedy(id: string, updates: Partial<Omit<Remedy, 'id' | 'created'>>): boolean {
    const index = this.remedies.findIndex(remedy => remedy.id === id);
    if (index !== -1) {
      this.remedies[index] = { 
        ...this.remedies[index], 
        ...updates, 
        updated: new Date().toISOString().split('T')[0] 
      };
      return true;
    }
    return false;
  }

  public deleteRemedy(id: string): boolean {
    const index = this.remedies.findIndex(remedy => remedy.id === id);
    if (index !== -1) {
      this.remedies.splice(index, 1);
      return true;
    }
    return false;
  }

  public getRemediesByCategory(category: string): Remedy[] {
    if (category === 'all') return this.getAllRemedies();
    return this.remedies.filter(remedy => remedy.category === category);
  }

  public searchRemedies(searchTerm: string): Remedy[] {
    if (!searchTerm.trim()) return this.getAllRemedies();
    
    const term = searchTerm.toLowerCase();
    return this.remedies.filter(remedy => 
      remedy.name.toLowerCase().includes(term) ||
      remedy.scientificName.toLowerCase().includes(term) ||
      remedy.commonNames.some(name => name.toLowerCase().includes(term)) ||
      remedy.mainIndications.some(indication => indication.toLowerCase().includes(term)) ||
      remedy.symptoms.some(symptom => symptom.toLowerCase().includes(term)) ||
      remedy.mentalSymptoms.some(symptom => symptom.toLowerCase().includes(term)) ||
      remedy.physicalSymptoms.some(symptom => symptom.toLowerCase().includes(term))
    );
  }

  public getTotalCount(): number {
    return this.remedies.length;
  }

  public getCategoryCount(category: string): number {
    if (category === 'all') return this.remedies.length;
    return this.remedies.filter(remedy => remedy.category === category).length;
  }
}

export default RemediesDataManager;
export type { Remedy };
